class AccordionMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.detailsElements = Array.from(this.querySelectorAll('details'));
    this.detailsElements.forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      summary.setAttribute(
        'aria-expanded',
        details.hasAttribute('open') ? 'true' : 'false'
      );

      const submenu = summary.nextElementSibling;
      if (submenu && submenu.id) summary.setAttribute('aria-controls', submenu.id);

      summary.addEventListener('click', this.onSummaryClick.bind(this), {
        capture: true,
      });

      summary.addEventListener('keydown', (event) => {
        if (event.code === 'Enter' || event.code === 'Space') {
          event.preventDefault();
          this.onSummaryClick(event);
          return;
        }

        if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.navigateSummaries(event, event.code === 'ArrowDown' ? 1 : -1);
        }
      });
    });

    this.addEventListener('keydown', (event) => {
      if (event.code !== 'Escape') return;
      const openDetails = this.querySelector('details[open]');
      if (!openDetails) return;
      this.closeDetail(openDetails);
      openDetails.querySelector('summary')?.focus();
    });
  }

  getScrollParent(element) {
    // Keep scrolling contained to the drawer instead of the whole page.
    let parent = element?.parentElement;
    while (parent && parent !== this) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight;
      if (isScrollable) return parent;
      parent = parent.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  ensureVisibleInScrollParent(element) {
    const scrollParent = this.getScrollParent(element);
    if (!scrollParent || scrollParent === document.scrollingElement) return;

    const elRect = element.getBoundingClientRect();
    const parentRect = scrollParent.getBoundingClientRect();

    const topDelta = elRect.top - parentRect.top;
    const bottomDelta = elRect.bottom - parentRect.bottom;

    // If element is above visible area, scroll up. If below, scroll down.
    if (topDelta < 0) {
      scrollParent.scrollTop += topDelta;
    } else if (bottomDelta > 0) {
      scrollParent.scrollTop += bottomDelta;
    }
  }

  preserveSummaryPosition(summary, callback) {
    const scrollParent = this.getScrollParent(summary);
    if (!scrollParent || scrollParent === document.scrollingElement) {
      callback();
      return;
    }

    const initialTop = summary.getBoundingClientRect().top;

    callback();

    let start = performance.now();
    const animate = (time) => {
      const currentTop = summary.getBoundingClientRect().top;
      const delta = currentTop - initialTop;

      if (Math.abs(delta) > 0.5) {
        scrollParent.scrollTop += delta;
      }

      if (time - start < 400) {
        requestAnimationFrame(animate);
      } else {
        const finalTop = summary.getBoundingClientRect().top;
        const parentTop = scrollParent.getBoundingClientRect().top;
        const parentBottom = scrollParent.getBoundingClientRect().bottom;
        
        if (finalTop < parentTop || finalTop > parentBottom - 40) {
          summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    };
    
    requestAnimationFrame(animate);
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    event.preventDefault();
    event.stopImmediatePropagation();

    const isOpen = details.hasAttribute('open');

    this.preserveSummaryPosition(summary, () => {
      if (isOpen) {
        this.closeDetail(details);
      } else {
        this.openDetail(details);
      }
    });
  }

  navigateSummaries(event, direction) {
    const currentSummary = event.currentTarget;
    const summaries = Array.from(this.querySelectorAll('summary'));
    const currentIndex = summaries.indexOf(currentSummary);
    if (currentIndex !== -1) {
      let nextIndex = currentIndex + direction;
      if (nextIndex >= summaries.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = summaries.length - 1;
      summaries[nextIndex].focus();
    }
  }

  openDetail(details) {
    details.setAttribute('open', '');
    const summary = details.querySelector('summary');
    summary.setAttribute('aria-expanded', 'true');
  }

  closeDetail(details) {
    const summary = details.querySelector('summary');
    summary.setAttribute('aria-expanded', 'false');
    details.removeAttribute('open');
  }
}

customElements.define('accordion-menu-drawer', AccordionMenuDrawer);
